import type { HoverPointerSnapshot } from "@/features/hoverIdle/input/hoverPointerTypes";
import type { WallId } from "@/features/hoverIdle/wall/wallDefinitions";

export interface WallActionHandlers {
  readonly onStartWall: (wallId: WallId) => boolean;
  readonly onStopWall: (wallId: WallId) => boolean;
}

interface CreateWallActionHandlersOptions {
  readonly startWall: (wallId: WallId) => boolean;
  readonly stopActiveWall: () => boolean;
  readonly refreshView: () => void;
  readonly saveAfterImportantEvent: () => void;
  readonly showHovTargetScreen: () => void;
  readonly waitForHovTargetLayout: () => Promise<void>;
  readonly canRefreshHovTargetHoverIntents: () => boolean;
  readonly getPointerSnapshot: () => HoverPointerSnapshot;
  readonly restoreVirtualPointerPosition: (
    position: NonNullable<HoverPointerSnapshot["position"]>,
  ) => void;
  readonly refreshHovTargetHoverIntents: () => void;
}

export function createWallActionHandlers(
  options: CreateWallActionHandlersOptions,
): WallActionHandlers {
  const refreshHoverAfterHovTargetLayout = (
    pointerBeforeTransition: HoverPointerSnapshot,
  ): void => {
    void options.waitForHovTargetLayout().then(() => {
      if (!options.canRefreshHovTargetHoverIntents()) {
        return;
      }
      restoreVirtualPointerIfNavigationClearedIt(
        pointerBeforeTransition,
        options,
      );
      options.refreshHovTargetHoverIntents();
    });
  };

  return {
    onStartWall(wallId: WallId): boolean {
      if (!options.startWall(wallId)) {
        return false;
      }
      const pointerBeforeTransition = options.getPointerSnapshot();
      options.refreshView();
      options.showHovTargetScreen();
      options.saveAfterImportantEvent();
      refreshHoverAfterHovTargetLayout(pointerBeforeTransition);
      return true;
    },

    onStopWall(_wallId: WallId): boolean {
      if (!options.stopActiveWall()) {
        return false;
      }
      options.refreshView();
      options.saveAfterImportantEvent();
      return true;
    },
  };
}

function restoreVirtualPointerIfNavigationClearedIt(
  pointerBeforeTransition: HoverPointerSnapshot,
  options: Pick<
    CreateWallActionHandlersOptions,
    "getPointerSnapshot" | "restoreVirtualPointerPosition"
  >,
): void {
  if (
    pointerBeforeTransition.kind !== "virtualTouch" ||
    pointerBeforeTransition.position === null
  ) {
    return;
  }
  const currentPointer = options.getPointerSnapshot();
  if (currentPointer.kind !== null && currentPointer.position !== null) {
    return;
  }
  options.restoreVirtualPointerPosition(pointerBeforeTransition.position);
}
